import { memo, useCallback, useMemo, useState } from "react"
import {
	IconArrowUpDownTrailFill18 as ArrowDownUp,
	IconOffice3Fill18 as Building2,
	IconCircleDashedFill18 as CircleDot,
	IconCircleHalfDashedCheckFill18 as CircleHalfDashedCheck,
	IconEnvelopeFill18 as Envelope,
	IconGlobeFill18 as Globe,
	IconHashtagFill18 as Hashtag,
	IconMapFill18 as MapIcon,
	IconMsgDottedFill18 as MsgDotted,
	IconArrowsRotateCenterFill18 as RotateCcwSquare,
	IconScrollFill18 as Scroll,
	IconTagsFill18 as Tags,
	IconUserFill18 as User2,
	IconUserPenFill18 as UserPen,
	IconUsersFill18 as Users,
} from "nucleo-ui-fill-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import {
	CustomerSizeIcon,
	CustomerTierIcon,
	SlackIconGray,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { DollarBillIcon } from "@productlane/ui/src/icons/project/dollarbill-icon"

import type { FilterType } from "./filter-popover-items/types"
import {
	FilterPopover,
	NumericComparisonFilterItems,
} from "@/components/filters"
import { analytics } from "@/lib/analytics"
import { useInboxAddresses } from "@/lib/use-inbox-addresses"
import { usePortalMarks } from "@/lib/use-portal-marks"
import { useSlackConnections } from "@/lib/zero"
import { SortIcon } from "@/screens/app/nav-icons"
import { Owner } from "@/screens/app/threads/components/right-bar/customer-properties/icons/owner"
import { Square } from "../components/right-bar/customer-properties/icons/square"
import { AssigneesFilterItems } from "./filter-popover-items/assignees-filter-items"
import { CompanyOwnerFilterItems } from "./filter-popover-items/company-owner-filter-items"
import { CompanyStatusFilterItems } from "./filter-popover-items/company-status-filter-items"
import { CompanyTierFilterItems } from "./filter-popover-items/company-tier-filter-items"
import { CreatorFilterItems } from "./filter-popover-items/creator-filter-items"
import { InboxFilterItems } from "./filter-popover-items/inbox-filter-items"
import { IssueStatusFilterItems } from "./filter-popover-items/issue-status-filter-items"
import { LinkedCompaniesFilterItems } from "./filter-popover-items/linked-companies-filter-items"
import { LinkedIssuesFilterItems } from "./filter-popover-items/linked-issues-filter-items"
import { LinkedPersonsFilterItems } from "./filter-popover-items/linked-persons-filter-items"
import { LinkedProjectsFilterItems } from "./filter-popover-items/linked-projects-filter-items"
import { LinkedTagsFilterItems } from "./filter-popover-items/linked-tags-filter-items"
import { OriginFilterItems } from "./filter-popover-items/origin-filter-items"
import { PortalFilterItems } from "./filter-popover-items/portal-filter-items"
import { PriorityFilterItems } from "./filter-popover-items/priority-filter-items"
import { ReplyStateFilterItems } from "./filter-popover-items/reply-state-filter-items"
import { SlackChannelFilterItems } from "./filter-popover-items/slack-channel-filter-items"
import { SlackWorkspaceFilterItems } from "./filter-popover-items/slack-workspace-filter-items"
import { StatusFilterItems } from "./filter-popover-items/status-filter-items"
import { InboxSortItems, useInboxSort } from "./inbox-sort"
import { useThreadsFilterState } from "./threads-filter-state"
import {
	useFilteredSearchItems,
	useSearchableFilterItems,
} from "./use-searchable-filter-items"

export const FILTER_CATEGORIES: NestedCommandGroup<FilterType>[] = [
	{ type: "Assignees", label: "Assignee", Icon: UserPen },
	{ type: "Origins", label: "Origin", Icon: RotateCcwSquare },
	{ type: "Portals", label: "Portal", Icon: Globe },
	{ type: "Inboxes", label: "Inbox", Icon: Envelope },
	{ type: "SlackWorkspaces", label: "Slack workspace", Icon: SlackIconGray },
	{ type: "SlackChannels", label: "Slack channel", Icon: Hashtag },
	{ type: "Tags", label: "Tag", Icon: Tags },
	{ type: "Projects", label: "Project", Icon: MapIcon },
	{ type: "Issues", label: "Issue", Icon: Scroll },
	{ type: "IssueStatuses", label: "Issue status", Icon: CircleHalfDashedCheck },
	{ type: "Priorities", label: "Priority", Icon: ArrowDownUp },
	{ type: "Creators", label: "Creator", Icon: User2 },
	{ type: "Companies", label: "Companies", Icon: Building2 },
	{ type: "People", label: "People", Icon: Users },
	{ type: "Statuses", label: "Status", Icon: CircleDot },
	{ type: "ReplyStates", label: "Response", Icon: MsgDotted },

	{ type: "CompanyRevenue", label: "Customer revenue", Icon: DollarBillIcon },
	{ type: "CompanyTiers", label: "Customer tier", Icon: CustomerTierIcon },
	{ type: "CompanyStatuses", label: "Customer status", Icon: Square },
	{ type: "CompanyOwners", label: "Customer owner", Icon: Owner },
	{ type: "CompanySize", label: "Customer size", Icon: CustomerSizeIcon },
]

const CATEGORIES_WITH_CUSTOM_FILTER: FilterType[] = [
	"Projects",
	"Tags",
	"Issues",
	"Companies",
	"People",
	"SlackChannels",
]

const DOT_BADGE_CATEGORIES: ReadonlySet<FilterType> = new Set([
	"CompanyRevenue",
	"CompanySize",
])

function NumericFilterSubPanel({
	activeFilter,
	onFilterItemSelect,
}: {
	activeFilter: "CompanyRevenue" | "CompanySize"
	onFilterItemSelect: () => void
}) {
	const { companyRevenue, setCompanyRevenue, companySize, setCompanySize } =
		useThreadsFilterState()
	return (
		<Command shouldFilter={false}>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandGroup>
					{activeFilter === "CompanyRevenue" && (
						<NumericComparisonFilterItems
							value={companyRevenue}
							onChange={setCompanyRevenue}
							onSelect={onFilterItemSelect}
							placeholder="Enter revenue..."
							prefix="$"
							suffix="ARR"
						/>
					)}
					{activeFilter === "CompanySize" && (
						<NumericComparisonFilterItems
							value={companySize}
							onChange={setCompanySize}
							onSelect={onFilterItemSelect}
							placeholder="Enter company size..."
						/>
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function FilterSubPanel({
	activeFilter,
	query,
	setQuery,
	onFilterItemSelect,
}: {
	activeFilter: FilterType
	query: string
	setQuery: (q: string) => void
	onFilterItemSelect: () => void
}) {
	if (activeFilter === "CompanyRevenue" || activeFilter === "CompanySize") {
		return (
			<NumericFilterSubPanel
				activeFilter={activeFilter}
				onFilterItemSelect={onFilterItemSelect}
			/>
		)
	}

	const hasCustomFilter = CATEGORIES_WITH_CUSTOM_FILTER.includes(activeFilter)

	return (
		<Command shouldFilter={!hasCustomFilter}>
			<CommandInput
				key={activeFilter}
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{activeFilter === "Assignees" && (
						<AssigneesFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Origins" && (
						<OriginFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Portals" && (
						<PortalFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "SlackWorkspaces" && (
						<SlackWorkspaceFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "SlackChannels" && (
						<SlackChannelFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{activeFilter === "Inboxes" && (
						<InboxFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Creators" && (
						<CreatorFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Priorities" && (
						<PriorityFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Statuses" && (
						<StatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "ReplyStates" && (
						<ReplyStateFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Projects" && (
						<LinkedProjectsFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{activeFilter === "Tags" && (
						<LinkedTagsFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{activeFilter === "Issues" && (
						<LinkedIssuesFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{activeFilter === "Companies" && (
						<LinkedCompaniesFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{activeFilter === "People" && (
						<LinkedPersonsFilterItems
							query={query}
							onSelect={onFilterItemSelect}
						/>
					)}
					{activeFilter === "IssueStatuses" && (
						<IssueStatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "CompanyTiers" && (
						<CompanyTierFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "CompanyStatuses" && (
						<CompanyStatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "CompanyOwners" && (
						<CompanyOwnerFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

type ThreadsFilterGroup = FilterType | "Sort"

const SORT_CATEGORY: NestedCommandGroup<ThreadsFilterGroup> = {
	type: "Sort",
	label: "Sort",
	Icon: SortIcon,
}

function SortSubPanel() {
	return (
		<Command shouldFilter={false}>
			<CommandList>
				<InboxSortItems />
			</CommandList>
		</Command>
	)
}

export const ThreadsFilter = memo(function ThreadsFilter({
	trigger,
	iconClassName,
	iconStrokeWidth = 1.75,
	subPanelSide,
	withSort = false,
	align,
	closeOnSelect = false,
}: {
	trigger?: React.ReactNode
	iconClassName?: string
	iconStrokeWidth?: number
	subPanelSide?: "left" | "right"
	/** Folds the inbox sort into this menu as its first entry, opened by v. */
	withSort?: boolean
	align?: "start" | "end"
	closeOnSelect?: boolean
} = {}) {
	// Runs even while the menu is closed, so a done view never keeps "Important first".
	useInboxSort()
	const [globalQuery, setGlobalQuery] = useState("")
	const filterState = useThreadsFilterState()
	const portalMarks = usePortalMarks()
	const inboxAddresses = useInboxAddresses()
	const slackWorkspaceCount = useSlackConnections().length

	const categories = useMemo<NestedCommandGroup<ThreadsFilterGroup>[]>(
		() => [
			...(withSort ? [SORT_CATEGORY] : []),
			...FILTER_CATEGORIES.filter(
				({ type }) =>
					(type !== "Portals" || portalMarks.length > 0) &&
					(type !== "Inboxes" || inboxAddresses.length > 0) &&
					(type !== "SlackWorkspaces" || slackWorkspaceCount > 1) &&
					(type !== "SlackChannels" || slackWorkspaceCount > 0),
			),
		],
		[withSort, portalMarks.length, inboxAddresses.length, slackWorkspaceCount],
	)

	const counts = useMemo(
		() =>
			({
				Assignees: filterState.assigneeIds.length,
				Origins: filterState.origins.length,
				Portals: filterState.portalInstanceIds.length,
				SlackWorkspaces: filterState.slackWorkspaceIds.length,
				SlackChannels: filterState.slackChannelIds.length,
				Inboxes: filterState.emailAddressIds.length,
				Tags: filterState.tagIds.length,
				Projects: filterState.projectIds.length,
				Issues: filterState.issueIds.length,
				IssueStatuses: filterState.issueStatuses.length,
				Priorities: filterState.painLevels.length,
				Creators: filterState.creatorIds.length,
				Companies: filterState.companyIds.length,
				People: filterState.personIds.length,
				Statuses: filterState.statuses.length,
				ReplyStates: filterState.replyStates.length,
				CompanyTiers: filterState.companyTiers.length,
				CompanyStatuses: filterState.companyStatuses.length,
				CompanyOwners: filterState.companyOwnerIds.length,
				CompanyRevenue: filterState.companyRevenue ? 1 : 0,
				CompanySize: filterState.companySize ? 1 : 0,
				Sort: 0,
			}) as Record<ThreadsFilterGroup, number>,
		[filterState],
	)

	const onCategorySelect = useCallback(() => {
		setGlobalQuery("")
	}, [])

	const [menuOpen, setMenuOpen] = useState(false)
	const allSearchableItems = useSearchableFilterItems({
		onCategorySelect: onCategorySelect as () => void,
		enabled: menuOpen,
		query: globalQuery,
	})
	const filteredSearchItems = useFilteredSearchItems(
		globalQuery,
		allSearchableItems,
		filterState,
	)

	const renderSubPanel = useCallback(
		(
			group: ThreadsFilterGroup,
			query: string,
			setQuery: (q: string) => void,
			onFilterItemSelect: () => void,
		) =>
			group === "Sort" ? (
				<SortSubPanel />
			) : (
				<FilterSubPanel
					activeFilter={group}
					query={query}
					setQuery={setQuery}
					onFilterItemSelect={onFilterItemSelect}
				/>
			),
		[],
	)

	const subPanelWidth = useCallback(
		(group: ThreadsFilterGroup) =>
			group === "Sort"
				? "w-48"
				: group === "CompanyRevenue" || group === "CompanySize"
					? "w-80"
					: "w-64",
		[],
	)

	return (
		<FilterPopover<ThreadsFilterGroup>
			categories={categories}
			counts={counts}
			renderSubPanel={renderSubPanel}
			searchableItems={allSearchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			dotBadgeCategories={DOT_BADGE_CATEGORIES}
			subPanelWidthClassName={subPanelWidth}
			subPanelSide={subPanelSide}
			align={align}
			closeOnSelect={closeOnSelect}
			groupShortcut={withSort ? { key: "v", group: "Sort" } : undefined}
			tooltipLabel={withSort ? "Filter and sort" : undefined}
			trigger={trigger}
			iconClassName={iconClassName}
			iconStrokeWidth={iconStrokeWidth}
			onOpen={() => analytics.track("filter-opened")}
			onOpenChange={setMenuOpen}
		/>
	)
})
